import type {
  CropMetadata,
  ImageCategory,
  ImageOcrStatus,
  Strain,
} from '@shared';
import { compressImageForUpload } from '../imageProcessing';
import { imagesApi, type ImageUploadOptions } from '../imagesApi';
import {
  getProductRepository,
  setProductRepositoryForTests,
  resetProductRepositoryForTests,
} from '../productRepository';
import {
  createLocalProductImageRepository,
  type LocalProductImageRepository,
} from './localProductImageRepository';
import { createIdbProductImageStore } from './idbProductImageStore';
import { createMemoryProductImageStore } from './memoryProductImageStore';

/**
 * Unified image API surface used by UI.
 * Local mode → IndexedDB; server mode → existing imagesApi.
 */
export interface ProductImagesApi {
  upload(
    strainId: string,
    file: Blob,
    imageCategory: ImageCategory,
    options?: ImageUploadOptions,
  ): Promise<Strain>;
  remove(imageId: string): Promise<{ deleted: boolean }>;
  setPrimary(imageId: string): Promise<Strain>;
}

let cachedLocalImages: LocalProductImageRepository | null = null;
let cachedApi: ProductImagesApi | null = null;
let testOverride: ProductImagesApi | null = null;
let testLocalImages: LocalProductImageRepository | null = null;

export function getLocalProductImageRepository(): LocalProductImageRepository {
  if (testLocalImages) return testLocalImages;
  if (!cachedLocalImages) {
    cachedLocalImages = createLocalProductImageRepository({
      store: createIdbProductImageStore(),
    });
  }
  return cachedLocalImages;
}

function createLocalProductImagesApi(
  images: LocalProductImageRepository,
): ProductImagesApi {
  return {
    async upload(strainId, file, imageCategory, _options = {}) {
      // Reuse existing package-capture preprocessing for large phone photos.
      const compressed = await compressImageForUpload(file);
      await images.add({
        productId: strainId,
        blob: compressed,
        imageCategory,
        originalFilename:
          file instanceof File && file.name ? file.name : null,
      });
      return getProductRepository().get(strainId);
    },
    async remove(imageId) {
      const result = await images.remove(imageId);
      return { deleted: result.deleted };
    },
    async setPrimary(imageId) {
      const updated = await images.setPrimary(imageId);
      return getProductRepository().get(updated.productId);
    },
  };
}

/**
 * Central access for product image operations.
 * Does not parse VITE env in callers — uses getProductRepository().mode.
 */
export function getProductImagesApi(): ProductImagesApi {
  if (testOverride) return testOverride;
  if (cachedApi) return cachedApi;
  const mode = getProductRepository().mode;
  cachedApi =
    mode === 'local'
      ? createLocalProductImagesApi(getLocalProductImageRepository())
      : imagesApi;
  return cachedApi;
}

export function setProductImagesApiForTests(api: ProductImagesApi): void {
  testOverride = api;
  cachedApi = null;
}

export function setLocalProductImageRepositoryForTests(
  repo: LocalProductImageRepository | null,
): void {
  testLocalImages = repo;
  cachedLocalImages = null;
  cachedApi = null;
}

export function resetProductImagesApiForTests(): void {
  testOverride = null;
  cachedLocalImages = null;
  cachedApi = null;
  // Preserve explicit local-image test override; clear only via
  // setLocalProductImageRepositoryForTests(null).
}

export {
  createLocalProductImageRepository,
  createMemoryProductImageStore,
  createIdbProductImageStore,
};

// Re-export test helpers used alongside product repository tests.
export {
  setProductRepositoryForTests,
  resetProductRepositoryForTests,
};

export type { ImageUploadOptions, CropMetadata, ImageOcrStatus };
